import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  FileText, 
  Download, 
  Loader2,
  FileCheck,
  ScrollText,
  UserPlus,
  ClipboardList,
  BookOpen,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const templateIcons = {
  informativa_privacy: ScrollText,
  consenso_dati: FileCheck,
  regolamento_volontari: BookOpen,
  modulo_iscrizione: UserPlus,
  accettazione_regolamento: ClipboardList,
  scheda_volontario: FileText,
  registro_volontari: BookOpen
};

export default function KitAmministrativo() {
  const [templates, setTemplates] = useState([]);
  const [volontari, setVolontari] = useState([]);
  const [disclaimer, setDisclaimer] = useState('');
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [selectedVolontario, setSelectedVolontario] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [templatesRes, volontariRes] = await Promise.all([
        fetch(`${API_URL}/api/kit-amministrativo/templates`, { credentials: 'include' }),
        fetch(`${API_URL}/api/volontari`, { credentials: 'include' })
      ]);

      if (templatesRes.ok) {
        const data = await templatesRes.json();
        setTemplates(data.templates || []);
        setDisclaimer(data.disclaimer || '');
      }
      
      if (volontariRes.ok) {
        setVolontari(await volontariRes.json());
      }
    } catch (error) {
      toast.error('Errore nel caricamento dei template');
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = async (templateId) => {
    setDownloading(templateId);
    try {
      let url = `${API_URL}/api/kit-amministrativo/genera/${templateId}`;
      if (selectedVolontario && selectedVolontario !== '__empty__') {
        url += `?volontario_id=${selectedVolontario}`;
      }

      const response = await fetch(url, { credentials: 'include' });
      
      if (!response.ok) {
        throw new Error('Errore durante il download');
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `${templateId}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);

      toast.success('Documento scaricato con successo');
    } catch (error) {
      toast.error('Errore durante il download del documento');
    } finally {
      setDownloading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#0055BB]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="kit-amministrativo-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Kit Amministrativo ETS
        </h1>
        <p className="text-slate-500 mt-1">
          Modelli e template pronti per la gestione amministrativa della tua associazione
        </p>
      </div>

      {/* Disclaimer */}
      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800">Avvertenza importante</p>
              <p className="text-xs text-amber-700 mt-1">{disclaimer}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Selezione Volontario (opzionale) */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Personalizzazione Documenti</CardTitle>
          <CardDescription>
            Seleziona un volontario per generare documenti precompilati con i suoi dati
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Select value={selectedVolontario} onValueChange={setSelectedVolontario}>
            <SelectTrigger className="w-full md:w-96" data-testid="select-volontario">
              <SelectValue placeholder="Nessun volontario (template vuoto)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__empty__">Nessun volontario (template vuoto)</SelectItem>
              {volontari.map((vol) => (
                <SelectItem key={vol.volontario_id} value={vol.volontario_id}>
                  {vol.cognome} {vol.nome} - {vol.codice_fiscale || 'N/D'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedVolontario && selectedVolontario !== '__empty__' && (
            <p className="text-sm text-green-600 mt-2">
              I documenti verranno generati con i dati del volontario selezionato
            </p>
          )}
        </CardContent>
      </Card>

      {/* Templates Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {templates.map((template) => {
          const IconComponent = templateIcons[template.id] || FileText;
          return (
            <Card key={template.id} className="hover:shadow-md transition-shadow" data-testid={`template-${template.id}`}>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-3 text-lg">
                  <div className="p-2 bg-[#0055BB]/10 rounded-lg">
                    <IconComponent className="w-5 h-5 text-[#0055BB]" />
                  </div>
                  {template.nome}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-500 mb-4 min-h-[40px]">
                  {template.descrizione}
                </p>
                <Button
                  onClick={() => downloadTemplate(template.id)}
                  disabled={downloading === template.id}
                  className="w-full"
                  data-testid={`download-${template.id}`}
                >
                  {downloading === template.id ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generazione...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Scarica PDF
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Info Footer */}
      <Card className="bg-slate-50">
        <CardContent className="pt-4">
          <p className="text-xs text-slate-500 text-center">
            I documenti generati sono in formato PDF e includono automaticamente i dati dell'ente.
            Per personalizzarli ulteriormente, seleziona un volontario prima del download.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
